import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Insights, Refresh } from "@mui/icons-material";
import { BarChart, BarDatum, BarSeries } from "../../components/charts/BarChart";
import { SourcesTable } from "../../components/orders/RequestSourcesCard";
import {
    Bucket,
    shopAnalyticsService,
    ShopChannels,
    ShopCustomers,
    ShopOnline,
} from "../../services/shopAnalyticsService";
import { formatCurrencyBDT } from "../../utils/currency";
import { PAYMENT_METHOD_LABELS } from "../../utils/paymentMethods";

// ── Periods ─────────────────────────────────────────────────────────────────

type Preset = "this_month" | "last_month" | "last_30" | "last_90" | "this_year";

const PRESETS: { key: Preset; label: string }[] = [
    { key: "this_month", label: "This month" },
    { key: "last_month", label: "Last month" },
    { key: "last_30", label: "Last 30 days" },
    { key: "last_90", label: "Last 90 days" },
    { key: "this_year", label: "This year" },
];

const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const presetRange = (preset: Preset): { from: string; to: string } => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const daysAgo = (n: number) => new Date(y, m, today.getDate() - n);
    switch (preset) {
        case "last_month":
            return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
        case "last_30":
            return { from: ymd(daysAgo(29)), to: ymd(today) };
        case "last_90":
            return { from: ymd(daysAgo(89)), to: ymd(today) };
        case "this_year":
            return { from: `${y}-01-01`, to: ymd(today) };
        default:
            return { from: ymd(new Date(y, m, 1)), to: ymd(today) };
    }
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Oct 5" / "Oct" under a bar, "Week of Oct 5" / "October 2026" in its tooltip. */
const bucketLabels = (iso: string, bucket: Bucket): { label: string; title: string } => {
    const [y, mo, d] = iso.split("-").map(Number);
    const month = MONTHS[mo - 1];
    if (bucket === "month") return { label: month, title: `${month} ${y}` };
    if (bucket === "week") return { label: `${month} ${d}`, title: `Week of ${month} ${d}, ${y}` };
    return { label: `${month} ${d}`, title: `${month} ${d}, ${y}` };
};

const pct = (n: number | null | undefined) => (n === null || n === undefined ? "—" : `${n}%`);
const money = (n: number) => formatCurrencyBDT(n);

const hours = (h: number | null) => {
    if (h === null) return "—";
    if (h < 1) return `${Math.round(h * 60)} min`;
    if (h < 48) return `${h.toFixed(1)} h`;
    return `${(h / 24).toFixed(1)} days`;
};

// ── Loading each section on its own ─────────────────────────────────────────

interface Period {
    from: string;
    to: string;
    fresh: boolean;
    /** Bumped by Refresh so the same dates load again. */
    nonce: number;
}

function useSection<T>(load: (from: string, to: string, fresh: boolean) => Promise<T>, period: Period) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        load(period.from, period.to, period.fresh)
            .then((d) => !cancelled && setData(d))
            .catch((err) => !cancelled && setError(err?.response?.data?.detail || "Couldn't load this section."))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);
    return { data, loading, error };
}

// ── Building blocks ─────────────────────────────────────────────────────────

const Section: React.FC<{
    title: string;
    note: string;
    loading: boolean;
    error: string;
    children: React.ReactNode;
}> = ({ title, note, loading, error, children }) => (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                {title}
            </Typography>
            {loading && <CircularProgress size={16} />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {note}
        </Typography>
        {error ? <Alert severity="error">{error}</Alert> : children}
    </Paper>
);

const Tiles: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
        sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" },
            gap: 1.5,
            mb: 2.5,
        }}
    >
        {children}
    </Box>
);

const Tile: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ label, value, sub }) => (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 1.5, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, fontWeight: 700, lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }} noWrap>
            {value}
        </Typography>
        {sub && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                {sub}
            </Typography>
        )}
    </Box>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {children}
    </Typography>
);

/** Label · count rows with a thin bar showing each one's share. */
const CountList: React.FC<{ rows: { label: string; count: number; extra?: string }[]; empty: string }> = ({ rows, empty }) => {
    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {empty}
            </Typography>
        );
    }
    const max = Math.max(...rows.map((r) => r.count));
    return (
        <Stack spacing={1}>
            {rows.map((r) => (
                <Box key={r.label}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            {r.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                            {r.count}
                            {r.extra && (
                                <Typography component="span" variant="caption" color="text.secondary">
                                    {" "}
                                    · {r.extra}
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                    <Box sx={{ height: 4, borderRadius: 2, bgcolor: "action.hover", mt: 0.5 }}>
                        <Box sx={{ height: 4, borderRadius: 2, bgcolor: "primary.main", width: `${max ? (r.count / max) * 100 : 0}%` }} />
                    </Box>
                </Box>
            ))}
        </Stack>
    );
};

const TwoColumns: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 2.5 }}>{children}</Box>
);

// ── Online orders ───────────────────────────────────────────────────────────

const OnlineSection: React.FC<{ data: ShopOnline }> = ({ data }) => {
    const chart: BarDatum[] = data.trend.map((t) => ({
        ...bucketLabels(t.bucket, data.bucket),
        values: { placed: t.placed, approved: t.approved },
    }));
    const series: BarSeries[] = [
        { key: "placed", label: "Placed", color: "#90caf9" },
        { key: "approved", label: "Approved", color: "#1e5f5a" },
    ];
    const bk = data.bkash;
    return (
        <>
            <Tiles>
                <Tile label="Requests placed" value={data.placed} sub={`${data.waiting} still waiting`} />
                <Tile
                    label="Approval rate"
                    value={pct(data.approval_rate)}
                    sub={`${data.approved} approved · ${data.rejected} rejected`}
                />
                <Tile label="Typical time to review" value={hours(data.median_review_hours)} sub="median, placed → approved/rejected" />
                <Tile
                    label="Approved value"
                    value={money(data.approved_value)}
                    sub={data.average_approved_value !== null ? `avg ${money(data.average_approved_value)}` : undefined}
                />
                <Tile
                    label="Website share of orders"
                    value={pct(data.online_share.percent)}
                    sub={`${data.online_share.orders} of ${data.online_share.all_orders} orders · ${pct(data.online_share.value_percent)} of value`}
                />
                <Tile
                    label="bKash payment success"
                    value={pct(bk.success_rate)}
                    sub={`${bk.paid} paid · ${bk.failed} failed · ${bk.abandoned} abandoned`}
                />
            </Tiles>

            <SubHeading>Requests over time</SubHeading>
            <Box sx={{ mb: 2.5 }}>
                <BarChart data={chart} series={series} />
            </Box>

            <TwoColumns>
                <Box>
                    <SubHeading>Why requests were rejected</SubHeading>
                    <CountList
                        rows={data.rejection_reasons.map((r) => ({ label: r.reason, count: r.count }))}
                        empty="No rejected requests in this period."
                    />
                </Box>
                <Box>
                    <SubHeading>Payment method chosen</SubHeading>
                    <CountList
                        rows={data.payment_methods.map((m) => ({
                            label: (PAYMENT_METHOD_LABELS as Record<string, string>)[m.method] || m.method,
                            count: m.count,
                            extra: data.placed ? `${Math.round((m.count / data.placed) * 100)}%` : undefined,
                        }))}
                        empty="No requests in this period."
                    />
                </Box>
            </TwoColumns>

            <SubHeading>Where requests came from</SubHeading>
            {data.sources.items.length > 0 ? (
                <Box sx={{ overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 2 }}>
                    <SourcesTable data={data.sources} />
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No requests in this period.
                </Typography>
            )}
        </>
    );
};

// ── Customers ───────────────────────────────────────────────────────────────

const RetentionTable: React.FC<{ cohorts: ShopCustomers["cohorts"] }> = ({ cohorts }) => (
    <Box sx={{ overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" sx={{ "& td, & th": { px: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" } }}>
            <TableHead>
                <TableRow>
                    <TableCell>First order</TableCell>
                    <TableCell align="right">Customers</TableCell>
                    {cohorts[0]?.rates.map((_, k) => (
                        <TableCell key={k} align="center">
                            {k === 0 ? "Month 0" : `+${k}`}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {cohorts.map((c) => {
                    const [y, m] = c.month.split("-").map(Number);
                    return (
                        <TableRow key={c.month}>
                            <TableCell>
                                {MONTHS[m - 1]} {y}
                            </TableCell>
                            <TableCell align="right">{c.customers}</TableCell>
                            {c.rates.map((rate, k) => (
                                <TableCell
                                    key={k}
                                    align="center"
                                    title={rate === null ? "" : `${c.retained[k]} of ${c.customers} ordered in this month`}
                                    sx={{
                                        // Shade by share; month 0 is always 100%, so keep it neutral.
                                        bgcolor:
                                            rate === null || k === 0 || c.customers === 0
                                                ? undefined
                                                : `rgba(30, 95, 90, ${Math.min(0.85, 0.08 + rate / 100)})`,
                                        color: rate !== null && k > 0 && rate >= 45 ? "#fff" : undefined,
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {rate === null || c.customers === 0 ? "" : `${Math.round(rate)}%`}
                                </TableCell>
                            ))}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </Box>
);

const CustomersSection: React.FC<{ data: ShopCustomers }> = ({ data }) => (
    <>
        <Tiles>
            <Tile label="Customers who ordered" value={data.active_customers} sub={`${data.new.orders + data.returning.orders} orders`} />
            <Tile label="New customers" value={data.new.customers} sub={`first order · ${money(data.new.value)}`} />
            <Tile label="Returning customers" value={data.returning.customers} sub={`ordered before · ${money(data.returning.value)}`} />
            <Tile label="Repeat rate" value={pct(data.repeat_rate)} sub="of these, ordered 2+ times ever" />
            <Tile
                label="Days to 2nd order"
                value={data.median_days_to_second_order === null ? "—" : `${Math.round(data.median_days_to_second_order)} days`}
                sub="median, all time"
            />
            <Tile label="Website sign-ups" value={data.signups} sub="new accounts this period" />
        </Tiles>
        {data.orders_without_customer > 0 && (
            <Alert severity="info" sx={{ mb: 2.5 }}>
                {data.orders_without_customer} order{data.orders_without_customer === 1 ? "" : "s"} in this period had no customer
                attached (e.g. walk-in sales), so {data.orders_without_customer === 1 ? "it isn't" : "they aren't"} counted
                above.
            </Alert>
        )}

        <SubHeading>Who came back</SubHeading>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Customers grouped by the month of their first order, and the share who ordered again in each month after.
            Last 6 months, whatever period is picked above.
        </Typography>
        <Box sx={{ mb: 2.5 }}>
            <RetentionTable cohorts={data.cohorts} />
        </Box>

        <TwoColumns>
            <Box>
                <SubHeading>Website orders by delivery area</SubHeading>
                <CountList
                    rows={data.delivery_areas.map((a) => ({
                        label: a.area,
                        count: a.requests,
                        extra: a.approved_value ? money(a.approved_value) : undefined,
                    }))}
                    empty="No website requests in this period."
                />
            </Box>
            <Box>
                <SubHeading>Pets of registered customers</SubHeading>
                {data.pets.species.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No pets added yet.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                            {data.pets.species.map((s) => (
                                <Chip key={s.species} label={`${s.species}: ${s.count}`} variant="outlined" />
                            ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {data.pets.owners} customer{data.pets.owners === 1 ? " has" : "s have"} added pets (all time).
                        </Typography>
                    </>
                )}
            </Box>
        </TwoColumns>
    </>
);

// ── Sales channels ──────────────────────────────────────────────────────────

const CHANNEL_COLORS = ["#1e5f5a", "#e65100", "#1565c0", "#6a1b9a", "#2e7d32", "#c62828", "#00838f", "#5d4037"];

const ChannelsSection: React.FC<{ data: ShopChannels }> = ({ data }) => {
    let i = 0;
    const series: BarSeries[] = data.channels.map((c) => ({
        key: c.name,
        label: c.name,
        color: c.name === "Not set" ? "#bdbdbd" : CHANNEL_COLORS[i++ % CHANNEL_COLORS.length],
    }));
    const chart: BarDatum[] = data.series.map((p) => ({ ...bucketLabels(p.bucket, data.bucket), values: p.values }));
    return (
        <>
            {data.not_set_order_share >= 25 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {Math.round(data.not_set_order_share)}% of orders in this period have no sales channel. Picking a channel when
                    creating an order makes this chart accurate.
                </Alert>
            )}
            {data.total_orders === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No orders in this period.
                </Typography>
            ) : (
                <>
                    <Box sx={{ mb: 2.5 }}>
                        <BarChart data={chart} series={series} stacked formatValue={(n) => money(n)} />
                    </Box>
                    <Box sx={{ overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 2 }}>
                        <Table size="small" sx={{ "& td, & th": { px: { xs: 1, sm: 2 } } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Channel</TableCell>
                                    <TableCell align="right">Orders</TableCell>
                                    <TableCell align="right">Value</TableCell>
                                    <TableCell align="right">Share</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.channels.map((c) => (
                                    <TableRow key={c.name}>
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: "2px",
                                                        bgcolor: series.find((s) => s.key === c.name)?.color,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                {c.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{c.orders}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                            {money(c.value)}
                                        </TableCell>
                                        <TableCell align="right">{pct(c.share)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>All channels</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        {data.total_orders}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                        {money(data.total_value)}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                </>
            )}
        </>
    );
};

// ── Page ────────────────────────────────────────────────────────────────────

export const ShopAnalytics: React.FC = () => {
    const initial = presetRange("this_month");
    const [preset, setPreset] = useState<Preset | "custom">("this_month");
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [period, setPeriod] = useState<Period>({ ...initial, fresh: false, nonce: 0 });

    const online = useSection(shopAnalyticsService.getOnline.bind(shopAnalyticsService), period);
    const customers = useSection(shopAnalyticsService.getCustomers.bind(shopAnalyticsService), period);
    const channels = useSection(shopAnalyticsService.getChannels.bind(shopAnalyticsService), period);

    const pickPreset = (p: Preset) => {
        const r = presetRange(p);
        setPreset(p);
        setFrom(r.from);
        setTo(r.to);
        setPeriod((prev) => ({ ...r, fresh: false, nonce: prev.nonce + 1 }));
    };

    const applyCustom = useCallback(() => {
        if (!from || !to) return;
        setPreset("custom");
        setPeriod((prev) => ({ from, to, fresh: false, nonce: prev.nonce + 1 }));
    }, [from, to]);

    const refresh = () => setPeriod((prev) => ({ ...prev, fresh: true, nonce: prev.nonce + 1 }));
    const anyLoading = online.loading || customers.loading || channels.loading;

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" }, display: "flex", alignItems: "center", gap: 1 }}>
                        <Insights color="primary" /> Shop Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Online orders, customers coming back, and sales by channel — from the orders you already record.
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<Refresh />} onClick={refresh} disabled={anyLoading}>
                    Refresh
                </Button>
            </Box>

            {/* Period */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                    {PRESETS.map((p) => (
                        <Chip
                            key={p.key}
                            label={p.label}
                            size="small"
                            color={preset === p.key ? "primary" : "default"}
                            variant={preset === p.key ? "filled" : "outlined"}
                            onClick={() => pickPreset(p.key)}
                        />
                    ))}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", ml: { md: "auto" }, width: { xs: "100%", md: "auto" } }}>
                        <TextField
                            type="date"
                            size="small"
                            label="From"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ flex: { xs: 1, md: "none" }, minWidth: 0, width: { md: 160 } }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="To"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ flex: { xs: 1, md: "none" }, minWidth: 0, width: { md: 160 } }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={applyCustom}
                            disabled={!from || !to || from > to}
                            // On a phone it takes its own line so the dates aren't cut off.
                            sx={{ width: { xs: "100%", md: "auto" } }}
                        >
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Section
                title="Online orders"
                note="Requests placed on the website in this period. Unpaid bKash attempts aren't counted as placed; approved value is the orders' current total, cancelled ones excluded."
                loading={online.loading}
                error={online.error}
            >
                {online.data && <OnlineSection data={online.data} />}
            </Section>

            <Section
                title="Customers & repeat buying"
                note="Orders with a customer attached, cancelled and draft orders excluded. New = their first ever order falls in this period."
                loading={customers.loading}
                error={customers.error}
            >
                {customers.data && <CustomersSection data={customers.data} />}
            </Section>

            <Section
                title="Sales channels over time"
                note="Value of all orders in this period by sales channel, cancelled and draft orders excluded."
                loading={channels.loading}
                error={channels.error}
            >
                {channels.data && <ChannelsSection data={channels.data} />}
            </Section>
        </Box>
    );
};

export default ShopAnalytics;
